import { ReactElement, useMemo, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
    Layer,
    Map as MapView,
    Marker,
    NavigationControl,
    Source
} from 'react-map-gl/mapbox';
import type { FeatureCollection, Polygon } from 'geojson';
import { DeviceConfigGeoAlert } from '@/api';
import {
    UIInlineAlert,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISegmentedControl
} from '@/ui';
import { useDevicesTranslation } from '../hooks';
import { MAPBOX_TOKEN } from '@/config';
import { blue, concrete, orange, white } from '@/ui/styles';
import { geoAlertRing, ringBounds } from '../util/geofence-geometry';

const MAPBOX_STYLE = 'mapbox://styles/mapbox/streets-v12';

export type GeofenceViewModalProps = {
    slot: number;
    current: DeviceConfigGeoAlert | undefined;
    /** The pending (not-yet-applied) shape, if any — shown in amber via a toggle. */
    pendingConfig?: DeviceConfigGeoAlert;
    isOpen: boolean;
    onClose: () => void;
};

export function GeofenceViewModal(props: GeofenceViewModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <ViewContent {...props} /> : null}
        </UIModal>
    );
}

function ViewContent(props: GeofenceViewModalProps): ReactElement {
    const { slot, current, pendingConfig, onClose } = props;
    const { t } = useDevicesTranslation();

    // Whenever a change is pending, show both tabs so the pending state is
    // obvious — greying out whichever side has no shape to preview (e.g. the
    // Current tab when the slot was previously disabled).
    const currentAvailable = !!current?.enabled;
    const pendingAvailable =
        !!pendingConfig?.enabled && pendingConfig.shape !== null;
    const showToggle =
        !!pendingConfig && (currentAvailable || pendingAvailable);

    const [mode, setMode] = useState<'current' | 'pending'>(
        pendingAvailable ? 'pending' : 'current'
    );

    const isPending = mode === 'pending';
    const active = isPending ? pendingConfig : current;
    const shapeColor = isPending ? orange : blue;

    const ring = useMemo(
        () => (active ? geoAlertRing(active) : null),
        [active]
    );

    const geometry = useMemo<FeatureCollection<Polygon> | null>(() => {
        if (!ring) return null;
        return {
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    properties: {},
                    geometry: { type: 'Polygon', coordinates: [ring] }
                }
            ]
        };
    }, [ring]);

    const bounds = useMemo(() => (ring ? ringBounds(ring) : null), [ring]);

    const centre = active?.shape === 'circle' ? active.points?.[0] : undefined;

    const detail = active ? buildDetail(active, t) : '';

    return (
        <>
            <UIModalHeader
                title={
                    isPending
                        ? t('components.geofence_view_modal.pending_title', {
                              number: slot + 1
                          })
                        : t('shared.devices.geofence_slot_label', {
                              number: slot + 1
                          })
                }
                subtitle={detail}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    {showToggle && (
                        <UISegmentedControl
                            items={[
                                {
                                    text: t(
                                        'components.geofence_view_modal.tab_current'
                                    ),
                                    disabled: !currentAvailable
                                },
                                {
                                    text: t('shared.labels.pending'),
                                    disabled: !pendingAvailable
                                }
                            ]}
                            selectedIndex={isPending ? 1 : 0}
                            selectedIndexChanged={(index) =>
                                setMode(index === 1 ? 'pending' : 'current')
                            }
                        />
                    )}
                    {!MAPBOX_TOKEN ? (
                        <UIInlineAlert
                            text={t('shared.devices.geofence_no_token')}
                        />
                    ) : !geometry || !bounds ? (
                        <UIInlineAlert
                            text={t('components.geofence_view_modal.no_shape')}
                        />
                    ) : (
                        <div
                            style={{
                                height: 400,
                                width: '100%',
                                overflow: 'hidden',
                                borderRadius: 8,
                                border: `1px solid ${concrete}`
                            }}
                        >
                            <MapView
                                key={mode}
                                mapboxAccessToken={MAPBOX_TOKEN}
                                mapStyle={MAPBOX_STYLE}
                                initialViewState={{
                                    bounds,
                                    fitBoundsOptions: {
                                        padding: 48,
                                        maxZoom: 16
                                    }
                                }}
                                style={{ width: '100%', height: '100%' }}
                                interactive={true}
                            >
                                <NavigationControl
                                    position="top-right"
                                    showCompass={false}
                                />
                                <Source
                                    id="geofence-view"
                                    type="geojson"
                                    data={geometry}
                                >
                                    <Layer
                                        id="geofence-view-fill"
                                        type="fill"
                                        paint={{
                                            'fill-color': shapeColor,
                                            'fill-opacity': 0.15
                                        }}
                                    />
                                    <Layer
                                        id="geofence-view-line"
                                        type="line"
                                        paint={{
                                            'line-color': shapeColor,
                                            'line-width': 2
                                        }}
                                    />
                                </Source>
                                {centre && (
                                    <Marker
                                        longitude={centre.longitude}
                                        latitude={centre.latitude}
                                    >
                                        <div
                                            style={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                backgroundColor: shapeColor,
                                                border: `2px solid ${white}`,
                                                boxShadow:
                                                    '0 1px 4px rgba(0,0,0,0.4)'
                                            }}
                                        />
                                    </Marker>
                                )}
                            </MapView>
                        </div>
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.close'),
                        onClick: onClose
                    }
                ]}
            />
        </>
    );
}

function buildDetail(
    slot: DeviceConfigGeoAlert,
    t: ReturnType<typeof useDevicesTranslation>['t']
): string {
    const parts: string[] = [];

    parts.push(
        slot.enabled ? t('shared.labels.enabled') : t('shared.labels.disabled')
    );

    if (slot.shape === 'circle') {
        parts.push(t('shared.devices.shape_circle'));
    } else if (slot.shape === 'polygon') {
        parts.push(t('shared.devices.shape_polygon'));
    }

    parts.push(
        slot.direction === 'out'
            ? t('shared.devices.direction_out')
            : t('shared.devices.direction_in')
    );

    if (slot.shape === 'circle' && slot.radius != null) {
        parts.push(t('shared.devices.radius_value', { value: slot.radius }));
    } else if (slot.shape === 'polygon') {
        parts.push(
            t('shared.devices.points_value', {
                count: slot.points?.length ?? 0
            })
        );
    }

    return parts.join(t('shared.devices.detail_separator'));
}
